import { useState, useRef, useEffect, useLayoutEffect, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  icon?: React.ReactNode;
}

interface SelectProps {
  label?: string;
  error?: string;
  hint?: string;
  icon?: LucideIcon;
  options: SelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  name?: string;
  required?: boolean;
  variant?: 'default' | 'minimal' | 'builder';
  triggerStyle?: React.CSSProperties;
  menuStyle?: React.CSSProperties;
  optionHoverBg?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      error,
      hint,
      icon: Icon,
      options,
      value,
      onChange,
      placeholder = 'Select...',
      disabled = false,
      className = '',
      name,
      required,
      variant = 'default',
      triggerStyle,
      menuStyle,
      optionHoverBg,
    },
    ref
  ) => {
    const isMinimal = variant === 'minimal';
    const isBuilder = variant === 'builder';
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const hasError = !!error;
    const selectedOption = options.find((opt) => opt.value === value);
    const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0, openUpward: false });

    // Calculate portal position when opening
    useLayoutEffect(() => {
      if (isOpen && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const maxMenuH = 240;
        const spaceBelow = window.innerHeight - rect.bottom - 8;
        const spaceAbove = rect.top - 8;
        const openUpward = spaceBelow < maxMenuH && spaceAbove > spaceBelow;
        setMenuPos({
          top: openUpward ? rect.top - 4 : rect.bottom + 4,
          left: rect.left,
          width: rect.width,
          openUpward,
        });
      }
    }, [isOpen]);

    // Close on click outside (check both container and portaled menu)
    useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        const target = event.target as Node;
        const inContainer = containerRef.current?.contains(target);
        const inMenu = menuRef.current?.contains(target);
        if (!inContainer && !inMenu) {
          setIsOpen(false);
        }
      }
      if (isOpen) document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    // Close on escape
    useEffect(() => {
      function handleEscape(event: KeyboardEvent) {
        if (event.key === 'Escape') setIsOpen(false);
      }
      if (isOpen) document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen]);

    const handleSelect = (optionValue: string) => {
      onChange?.(optionValue);
      setIsOpen(false);
    };

    return (
      <div className={`w-full space-y-1.5 ${className}`}>
        {label && (
          <label className={isBuilder ? 'block text-xs font-medium text-text-secondary' : 'block text-sm font-medium text-text-primary'}>
            {label}
          </label>
        )}

        <div ref={containerRef} className="relative">
          {/* Hidden native select for form submission and accessibility */}
          <select
            ref={ref}
            name={name}
            value={value || ''}
            onChange={(e) => onChange?.(e.target.value)}
            required={required}
            disabled={disabled}
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
          >
            <option value="" disabled>{placeholder}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Custom trigger button */}
          <button
            type="button"
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            className={
              isMinimal
                ? `w-full px-2 pr-8 py-2 ${Icon ? 'pl-9' : ''} bg-transparent border ${hasError ? 'border-error' : isOpen ? 'border-current' : 'border-current/30'} rounded text-left text-sm focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200`
                : isBuilder
                ? `w-full h-10 px-3 ${Icon ? 'pl-10' : ''} pr-8 bg-background-card border ${hasError ? 'border-error' : isOpen ? 'border-primary ring-2 ring-primary/20' : 'border-primary/30 hover:border-primary'} rounded-lg text-sm text-left focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200`
                : `w-full h-10 px-4 ${Icon ? 'pl-11' : ''} pr-8 bg-background-card border ${hasError ? 'border-error' : isOpen ? 'border-primary ring-2 ring-primary/20' : 'border-border hover:border-primary'} rounded-lg text-sm text-left focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200`
            }
            style={triggerStyle}
          >
            {Icon && (
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none">
                <Icon size={18} />
              </span>
            )}
            <span className={isMinimal ? (selectedOption ? '' : 'opacity-50') : (selectedOption ? 'text-text-primary' : 'text-text-secondary')}>
              {selectedOption ? (
                <span className="flex items-center gap-2">
                  {selectedOption.icon}
                  {selectedOption.label}
                </span>
              ) : (
                placeholder
              )}
            </span>
            <ChevronDown
              className={`absolute ${isMinimal ? 'right-2 w-4 h-4' : 'right-3 w-4 h-4'} top-1/2 -translate-y-1/2 ${isMinimal ? 'opacity-50' : 'text-text-secondary'} transition-transform duration-200 ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </button>
        </div>

        {(error || hint) && (
          <p className={`text-sm ${hasError ? 'text-error' : 'text-text-secondary'}`}>
            {error || hint}
          </p>
        )}

        {/* Portaled dropdown menu */}
        {createPortal(
          <AnimatePresence>
            {isOpen && (
              <motion.div
                ref={menuRef}
                initial={{ opacity: 0, y: menuPos.openUpward ? 10 : -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: menuPos.openUpward ? 10 : -10, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                role="listbox"
                className={
                  isMinimal
                    ? 'fixed bg-white border border-gray-200 rounded shadow-lg overflow-hidden z-[9999]'
                    : 'fixed bg-background-card border border-border rounded-lg shadow-lg overflow-hidden z-[9999]'
                }
                style={{
                  top: menuPos.openUpward ? undefined : menuPos.top,
                  bottom: menuPos.openUpward ? window.innerHeight - menuPos.top : undefined,
                  left: menuPos.left,
                  width: menuPos.width,
                  ...menuStyle,
                  ...(optionHoverBg ? { '--option-hover-bg': optionHoverBg, '--option-selected-bg': optionHoverBg } as React.CSSProperties : {}),
                }}
              >
                <div className="py-1 max-h-60 overflow-y-auto">
                  {options.map((option) => {
                    const isSelected = option.value === value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => !option.disabled && handleSelect(option.value)}
                        disabled={option.disabled}
                        className={
                          isMinimal
                            ? `w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors ${option.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${optionHoverBg ? `themed-select-option${isSelected ? ' is-selected' : ''}` : isSelected ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`
                            : `w-full flex items-center gap-3 px-4 py-2 text-left text-sm transition-colors ${option.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${isSelected ? 'bg-primary/10 text-primary' : 'text-text-primary hover:bg-background-surface'}`
                        }
                      >
                        {option.icon && <span className="flex-shrink-0">{option.icon}</span>}
                        <span className="flex-1">{option.label}</span>
                        {isSelected && <Check className="w-4 h-4 flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
